import { Component, OnDestroy, OnInit } from '@angular/core';
import { map, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BaseIndexPageComponent } from '../../../../components/pages/base-index-page/base-index-page.component';
import BaseIndexPageConfig from '../../../../components/pages/base-index-page/base-index-page.config';
import { DataTableColumnActions } from '../../../../components/widgets/data-table/columns/data-table-column-actions/data-table-column-actions.config';
import { DataTableColumnText } from '../../../../components/widgets/data-table/columns/data-table-column-text/data-table-column-text.config';

import ApiOptionsConfig from '../../../../services/api/api-options.config';
import { QualityManagementService } from '../../../../services/quality-management.service';
import { RouteService } from '../../../../services/route.service';
import { MainLayoutService } from '../../../../services/main-layout.service';
import { AccessControlService } from '../../../../services/access-control.service';
import { CommonService } from '../../../../services/common.service';

import { Risk, riskPriorityLabel, riskStatusLabel, riskTypeLabel } from '../../../../models/entities/risk';

@Component({
  selector: 'app-risk-management-index-page',
  imports: [BaseIndexPageComponent],
  templateUrl: './risk-management-index-page.component.html',
  styleUrl: './risk-management-index-page.component.scss',
})
export class RiskManagementIndexPageComponent implements OnInit, OnDestroy {

  baseIndexPageConfig: BaseIndexPageConfig;

  private activeProfileCode: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    protected qualityManagementService: QualityManagementService,
    private routeService: RouteService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService,
    private commonService: CommonService
  ) {
    this.baseIndexPageConfig = {
      pageTitle: 'Risk Management',
      cardTitle: 'Risk',
      dataTable: {
        data: [],
        searchModel: {
          code: null,
          type: null,
          date: null,
          description: null,
          status: null,
          nextReview: null,
          opportunity: null,
          threat: null,
          priority: null,
        },
        pagination: null,
        columns: [
          new DataTableColumnText({
            attribute: 'code',
            header: 'Code',
            filter: 'text',
            value: (item: Risk) => item?.code ?? ''
          }),
          new DataTableColumnText({
            attribute: 'type',
            header: 'Type',
            filter: 'select',
            filterOptionsSource: 'riskTypes',
            value: (item: Risk) => riskTypeLabel(item?.type) || '-'
          }),
          new DataTableColumnText({
            attribute: 'date',
            header: 'Date',
            filter: 'text',
            value: (item: Risk) => this.formatDate(item?.date)
          }),
          new DataTableColumnText({
            attribute: 'description',
            header: 'Description',
            filter: 'text',
            value: (item: Risk) => item?.description ?? ''
          }),
          new DataTableColumnText({
            attribute: 'status',
            header: 'Status',
            filter: 'select',
            filterOptionsSource: 'riskStatuses',
            value: (item: Risk) => riskStatusLabel(item?.status) || '-'
          }),
          new DataTableColumnText({
            attribute: 'nextReview',
            header: 'Next Review',
            filter: 'text',
            value: (item: Risk) => this.formatDate(item?.nextReview)
          }),
          new DataTableColumnText({
            attribute: 'opportunity',
            header: 'Opportunity',
            filter: 'text',
            value: (item: Risk) => item?.opportunity ?? ''
          }),
          new DataTableColumnText({
            attribute: 'threat',
            header: 'Threat',
            filter: 'text',
            value: (item: Risk) => item?.threat ?? ''
          }),
          new DataTableColumnText({
            attribute: 'priority',
            header: 'Priority',
            filter: 'select',
            filterOptionsSource: 'riskPriorities',
            value: (item: Risk) => riskPriorityLabel(item?.priority) || '-'
          }),
          new DataTableColumnActions({
            header: 'Actions',
            attribute: 'actions',
            style: { width: '140px' },
            buttons: [
              {
                icon: 'fa-eye',
                tooltip: 'View risk details',
                onClick: (item: Risk) => {
                  this.routeService.riskManagementDetail(Number(item.riskId));
                }
              },
              {
                icon: 'fa-edit',
                tooltip: 'Edit risk',
                onClick: (item: Risk) => {
                  this.routeService.riskManagementUpdate(Number(item.riskId));
                },
                hidden: () => !this.canModify()
              },
              {
                icon: 'fa-trash',
                tooltip: 'Delete risk',
                onClick: () => { },
                isDelete: true,
                hidden: () => !this.canDelete()
              },
            ]
          })
        ]
      },
      apiListService: (options: ApiOptionsConfig) => this.qualityManagementService.listRisks(options).pipe(
        map((response: any) => ({
          ...response,
          data: {
            risks: this.qualityManagementService.readResponseKey(response, 'risks') ?? []
          },
          pagination: response?.pagination ?? response?.data?.pagination ?? null
        }))
      ),
      responseListModelName: 'risks',
    }
  }

  ngOnInit(): void {
    this.mainLayoutService.activeProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profileCode => {
        this.activeProfileCode = profileCode;

        if (this.canModify()) {
          this.baseIndexPageConfig.addNewItemButton = {
            type: 'success',
            title: 'New Risk',
            onClick: () => this.routeService.riskManagementCreate()
          };
        } else {
          this.baseIndexPageConfig.addNewItemButton = undefined;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private formatDate(value?: string | null): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    return isNaN(date.getTime()) ? '-' : this.commonService.formatDateOnly(date);
  }

  private canModify(): boolean {
    return this.accessControlService.hasFeatureActionAccess(
      this.activeProfileCode,
      'QUALITY_MANAGEMENT_RISKS',
      'modify'
    );
  }

  private canDelete(): boolean {
    return this.accessControlService.hasFeatureActionAccess(
      this.activeProfileCode,
      'QUALITY_MANAGEMENT_RISKS',
      'delete'
    );
  }
}
